import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  createAdminClient,
  createBusinessClient,
  createCustomerClient,
  createMerchantClient,
} from '@beatq/sdk';
import type { ApiSurface } from '../auth/login.js';
import { useAuth } from '../auth/AuthProvider.js';
import { csrfHeaders } from '../api/envelope.js';
import {
  assertPermCode,
  hasPermission,
  isPermCode,
  parsePermissionsFromToken,
  type PermissionClaims,
  resolvePermissionState,
  type PermissionState,
} from './resolve.js';

export type { ApiSurface } from '../auth/login.js';

interface PermissionContextValue {
  claims: PermissionClaims;
  authenticated: boolean;
  loading: boolean;
  /** True when `/auth/me/permissions` failed; JWT claims still apply. */
  permissionsLoadFailed: boolean;
  disabledPermissions: readonly string[];
  hasPerm: (perm: string) => boolean;
  stateFor: (perm?: string) => PermissionState;
}

const PermissionContext = createContext<PermissionContextValue>({
  claims: { permissions: [] },
  authenticated: false,
  loading: false,
  permissionsLoadFailed: false,
  disabledPermissions: [],
  hasPerm: () => false,
  // Delegate rather than hiding unconditionally: an ungated region (no PERM code)
  // must still render outside a provider, or a whole screen silently blanks.
  stateFor: (perm) => resolvePermissionState({ permissions: [] }, perm, false),
});

export interface PermissionProviderProps {
  surface: ApiSurface;
  children: ReactNode;
  /** PERM codes shown as present-but-disabled (07-01). */
  disabledPermissions?: readonly string[];
}

function getApiBaseUrl(): string {
  if (typeof import.meta !== 'undefined') {
    const env = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env;
    if (env?.VITE_API_BASE_URL) return env.VITE_API_BASE_URL;
  }
  return '';
}

async function fetchPermissions(
  baseUrl: string,
  surface: ApiSurface,
  token: string,
): Promise<string[]> {
  const opts = {
    baseUrl,
    credentials: 'include' as RequestCredentials,
    headers: csrfHeaders(),
    accessToken: token,
  };
  const { data, error, response } =
    surface === 'customer'
      ? await createCustomerClient(opts).GET('/customer/v1/auth/me/permissions')
      : surface === 'merchant'
        ? await createMerchantClient(opts).GET('/merchant/v1/auth/me/permissions')
        : surface === 'admin'
          ? await createAdminClient(opts).GET('/admin/v1/auth/me/permissions')
          : await createBusinessClient(opts).GET('/business/v1/auth/me/permissions');

  if (!response.ok || error) {
    throw new Error(`permissions_load_failed:${response.status}`);
  }
  const envelope = data as { data?: { permissions?: string[] } } | undefined;
  return (envelope?.data?.permissions ?? []).filter(isPermCode);
}

export function PermissionProvider({
  surface,
  children,
  disabledPermissions = [],
}: PermissionProviderProps) {
  const { accessToken: token } = useAuth();
  const [apiPerms, setApiPerms] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [permissionsLoadFailed, setPermissionsLoadFailed] = useState(false);

  useEffect(() => {
    if (!token) {
      setApiPerms([]);
      setPermissionsLoadFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchPermissions(getApiBaseUrl(), surface, token)
      .then((permissions) => {
        if (cancelled) return;
        setApiPerms(permissions);
        setPermissionsLoadFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Keep prior API grants + JWT; do not pretend the API returned [].
        setPermissionsLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, surface]);

  const value = useMemo(() => {
    const tokenClaims = parsePermissionsFromToken(token);
    const merged = [...new Set([...tokenClaims.permissions, ...apiPerms])];
    const claims = { permissions: merged };
    const authenticated = Boolean(token);
    const disabled = disabledPermissions.filter(isPermCode);

    return {
      claims,
      authenticated,
      loading,
      permissionsLoadFailed,
      disabledPermissions: disabled,
      hasPerm: (perm: string) => {
        assertPermCode(perm);
        return hasPermission(claims, perm);
      },
      stateFor: (perm?: string) => {
        if (perm) assertPermCode(perm);
        return resolvePermissionState(claims, perm, authenticated, { disabled });
      },
    };
  }, [token, apiPerms, loading, permissionsLoadFailed, disabledPermissions]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

/** JWT claims + `/auth/me/permissions` merged per MOD-003. */
export function usePermissions(): PermissionContextValue {
  return useContext(PermissionContext);
}

export function usePermissionState(perm?: string): PermissionState {
  const { stateFor } = usePermissions();
  return stateFor(perm);
}
